import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * A way to keep the work *and* go, offered beside Discard and Keep editing.
 *
 * `pending` while it runs: the button stays put and is disabled, so a second
 * press cannot start a second save.
 */
export interface DiscardDialogPrimaryAction {
  label: string;
  onSelect: () => void;
  pending?: boolean;
}

/**
 * The one "Discard changes?" confirmation (spec 129 FR-006), Keep editing
 * being the default. `useDiscardGuard` renders it for sheets; the workflow
 * builder renders it for a blocked navigation.
 *
 * With a `primaryAction` (spec 144 FR-001), that action is the default
 * instead: it carries the filled button and the focus, and Keep editing steps
 * back to outline. Without one, nothing changes for the dialog's other users.
 */
export function DiscardChangesDialog({
  open,
  onDiscard,
  onKeep,
  description = 'What you typed in this form will be lost.',
  primaryAction,
}: {
  open: boolean;
  onDiscard: () => void;
  onKeep: () => void;
  description?: string;
  primaryAction?: DiscardDialogPrimaryAction;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onKeep();
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Discard changes?</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onDiscard}>
            Discard
          </Button>
          {primaryAction ? (
            <>
              <Button variant="outline" onClick={onKeep}>
                Keep editing
              </Button>
              <Button
                autoFocus
                disabled={primaryAction.pending}
                onClick={primaryAction.onSelect}
              >
                {primaryAction.label}
              </Button>
            </>
          ) : (
            <Button autoFocus onClick={onKeep}>
              Keep editing
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
